"use client";

import { useState, useEffect, useRef } from "react";
import { storyblokEditable, StoryblokComponent } from "@storyblok/react/rsc";
import Autoplay from "embla-carousel-autoplay";
import { useLightboxOpen } from "@/components/layout/LightboxProvider";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";

type TopicGalleryStoryblok = {
  headline?: string;
  topics?: any[];
  _uid: string;
  component: "topic_gallery";
  [k: string]: any;
};

type TopicGalleryProps = {
  blok: TopicGalleryStoryblok;
};

const TopicGallery = ({ blok }: TopicGalleryProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const lightboxOpen = useLightboxOpen();
  const autoplayRef = useRef(
    Autoplay({
      delay: 3000,
      stopOnInteraction: true,
      stopOnMouseEnter: true,
    })
  );

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const autoplay = autoplayRef.current;
    if (!api) return;
    try {
      if (isInView && !lightboxOpen) {
        autoplay.play();
      } else {
        autoplay.stop();
      }
    } catch {
      // Carousel may not be active (e.g. hidden on desktop)
    }
  }, [api, isInView, lightboxOpen]);

  return (
    <div ref={sectionRef} {...storyblokEditable(blok)} className="w-full">
      <section className="py-6 flex flex-col gap-4 w-full items-center rounded-3xl">
        {blok.headline && (
          <h2 className="text-headline-xl text-black text-center px-4 mb-2">
            {blok.headline}
          </h2>
        )}

        {/* Mobile: scrollable carousel */}
        <div className="md:hidden w-full">
          <Carousel
            setApi={setApi}
            opts={{
              align: "center",
              loop: false,
              dragFree: false,
              watchResize: true,
            }}
            plugins={[autoplayRef.current]}
            className="w-full"
          >
            <CarouselContent className="ml-0 items-stretch">
              {blok.topics?.map((nestedBlok: any) => (
                <CarouselItem
                  key={nestedBlok._uid}
                  className="pl-0 basis-auto h-auto"
                >
                  <StoryblokComponent blok={nestedBlok} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>

        {/* Tablet & Desktop: static grid */}
        <div className="hidden md:flex flex-wrap justify-center px-8 max-w-7xl mx-auto">
          {blok.topics?.map((nestedBlok: any) => (
            <div key={nestedBlok._uid}>
              <StoryblokComponent blok={nestedBlok} />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default TopicGallery;
